import { useFormik } from 'formik';
import { useEffect, useRef, useState } from 'react';
import Markdown from 'react-markdown';
import { useLocation, useParams } from 'react-router-dom';
import { toast } from 'react-toastify';
import Input from '../../components/form/Input';
import { useAuth } from '../../context/authContext';
import useAxiosFunction from '../../hooks/useAxiosFunction';
import { useResourceUsage } from '../../context/ResourceUsageContext';
// import usersDb, { TUser } from '../../mocks/db/users.db';

interface ChatPageProps {
	soapNoteId?: string;
}

const ChatPage = ({ soapNoteId }: ChatPageProps) => {
	const { id } = useParams();
	const { userData, token } = useAuth();
	const messagesEndRef = useRef<HTMLDivElement>(null);
	const location = useLocation();
	const [, error, loading, axiosFetch] = useAxiosFunction();

	// Access the soapId from the URL or from the state
	const { pathname } = location;
	const urlParts = pathname.split('/');
	const soapIdFromUrl = urlParts[urlParts.indexOf('soap') + 1];

	const { setUsage } = useResourceUsage();

	// Initial welcome message from the AI
	const [messages, setMessages] = useState([
		{
			message: "Hello! I'm your AI assistant for this patient's SOAP note. How can I help you today?",
			isAnswer: true
		}
	]);

	const [isTyping, setIsTyping] = useState(false);
	const [apiError, setApiError] = useState<string | null>(null);

	// Auto-scroll to the bottom of the chat when new messages are added
	useEffect(() => {
		scrollToBottom();
	}, [messages]);

	const scrollToBottom = () => {
		messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
	};

	const formik = useFormik({
		onSubmit(): void | Promise<never> {
			if (!formik.values.textField.trim()) return;

			const userMessage = { message: formik.values.textField, isAnswer: false };
			setMessages([...messages, userMessage]);
			formik.resetForm();

			// Call the actual API instead of simulating
			callChatAPI(formik.values.textField);
		},
		initialValues: {
			textField: '',
		},
	});

	// Call the actual chat API
	const callChatAPI = async (userQuery: string) => {
		setIsTyping(true);
		setApiError(null);

		try {
			// Use the SOAP note ID from props, URL
			const finalSoapNoteId = soapNoteId || soapIdFromUrl;
			
			// Get the auth token from the authContext
			if (!token) {
				throw new Error('No authentication token available');
			}

			// Use axiosFetch instead of fetch
			const data = await axiosFetch({
				method: 'POST',
				url: '/patient/chat',
				headers: {
					'deviceType': 'WEB',
					'Authorization': `Bearer ${token}`
				},
				body: {
					noteId: finalSoapNoteId,
					message: userQuery
				}
			});

			// Add the API response to the chat
			if (data) {
				setMessages(prev => [...prev, { message: data.data.response, isAnswer: true }]);
				setUsage(prev => ({
					...prev,
					chatCount: data.data.chatCount || prev.chatCount
				}));
			}
		} catch (error:any) {
			console.error('Error calling chat API:', error);
			setApiError('Failed to get a response from the AI assistant. Please try again.');
			// toast.error('Failed to get a response from the AI. Please try again.');
			toast.error(error?.data?.msg || error.message);

			// Add a fallback error message to the chat
			setMessages(prev => [...prev, {
				message: "I'm sorry, I encountered an error while processing your request. Please try again later.",
				isAnswer: true
			}]);
		} finally {
			setIsTyping(false);
		}
	};

	return (
		<div className="flex flex-col h-full">
			<div className="flex-1 overflow-y-auto bg-gray-50 dark:bg-slate-900/50 rounded-t-lg shadow-inner">
				<div className="p-4">
					{messages.map((item, index) => (
						<div
							key={index}
							className={`flex mb-4 ${item.isAnswer ? 'justify-start' : 'justify-end'}`}
						>
							{item.isAnswer && (
								<div className="h-8 w-8 rounded-full bg-blue-500 flex-shrink-0 mr-2 flex items-center justify-center text-white font-bold">
									AI
								</div>
							)}
							<div
								className={`max-w-[80%] rounded-2xl px-4 py-3 ${
									item.isAnswer
										? 'bg-white dark:bg-slate-800 text-gray-800 dark:text-gray-200 shadow-sm'
										: 'bg-blue-500 text-white shadow-sm'
								}`}
							>
							 <Markdown  children={item.message} />
							</div>
							{!item.isAnswer && (
								<div className="h-8 w-8 rounded-full bg-gray-200 dark:bg-slate-700 flex-shrink-0 ml-2 flex items-center justify-center text-gray-700 dark:text-gray-300 font-bold">
									{userData?.firstName?.charAt(0) || 'U'}
								</div>
							)}
						</div>
					))}
					{isTyping && (
						<div className="flex justify-start mb-4">
							<div className="h-8 w-8 rounded-full bg-blue-500 flex-shrink-0 mr-2 flex items-center justify-center text-white font-bold">
								AI
							</div>
							<div className="bg-white dark:bg-slate-800 text-gray-800 dark:text-gray-200 rounded-2xl px-4 py-3 shadow-sm">
								<div className="flex space-x-1">
									<div className="h-2 w-2 bg-gray-500 dark:bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: '0ms' }}></div>
									<div className="h-2 w-2 bg-gray-500 dark:bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: '300ms' }}></div>
									<div className="h-2 w-2 bg-gray-500 dark:bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: '600ms' }}></div>
								</div>
							</div>
						</div>
					)}
					<div ref={messagesEndRef} />
				</div>
			</div>

			<div className="mt-auto sticky bottom-0 p-4 bg-white dark:bg-slate-800 border-t border-gray-200 dark:border-gray-700">
				<form onSubmit={(e) => { e.preventDefault(); formik.submitForm(); }} className="flex items-center gap-2">
					<div className="relative flex-1">
						<Input
							id="textField"
							name="textField"
							dimension="lg"
							placeholder="Ask a question about this patient's SOAP note..."
							onChange={formik.handleChange}
							value={formik.values.textField}
							className="w-full pr-12 border-gray-300 dark:border-gray-600 rounded-lg"
						/>
						<button
							type="submit"
							disabled={!formik.values.textField.trim() || isTyping}
							className={`absolute right-2 top-1/2 transform -translate-y-1/2 p-2 rounded-full ${
								formik.values.textField.trim() && !isTyping
									? 'bg-blue-500 text-white hover:bg-blue-600'
									: 'bg-gray-200 text-gray-400 cursor-not-allowed'
							} transition-colors`}
						>
							<svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
								<path d="M10.894 2.553a1 1 0 00-1.788 0l-7 14a1 1 0 001.169 1.409l5-1.429A1 1 0 009 15.571V11a1 1 0 112 0v4.571a1 1 0 00.725.962l5 1.428a1 1 0 001.17-1.408l-7-14z" />
							</svg>
						</button>
					</div>
				</form>
				<div className="mt-2 text-xs text-gray-500 dark:text-gray-400 flex justify-between">
					<span>{apiError ? 'Connection issue. Retrying...' : 'Connected to AI assistant'}</span>
					<button
						className="text-blue-500 hover:text-blue-700 dark:text-blue-400 dark:hover:text-blue-300 transition-colors"
						onClick={() => setMessages([{
							message: "Hello! I'm your AI assistant for this patient's SOAP note. How can I help you today?",
							isAnswer: true
						}])}
					>
						Clear chat
					</button>
				</div>
			</div>
		</div>
	);
};

export default ChatPage;
